import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TourCallout from './TourCallout';
import {
  clickUntilCentered,
  clickUntilVisible,
  measure,
  scrollIntoView,
  sleep,
  waitForScrollSettle,
  waitForStableRect,
  waitForSelector,
  type AnchorRect,
} from './anchors';
import type { TourAction, TourDefinition, TourOverlay } from './tour.types';

interface TourControllerProps {
  tour: TourDefinition;
  /** 'onboarding' prefers each overlay's onboardingBody copy. */
  mode?: 'demo' | 'onboarding';
  onComplete?: () => void;
  onExit?: () => void;
  /** Fires once a step's overlay is actually on screen. */
  onStepView?: (stepId: string) => void;
  /** Fires when a CTA button (e.g. Start Free Trial) is clicked. */
  onCta?: (label: string) => void;
}

/**
 * Walks a tour: drives the real router to each step's route, applies any preActions,
 * waits for the anchor, then renders the callout. Knows nothing about demo mode, so the
 * same component can run against live data.
 */
const TourController: React.FC<TourControllerProps> = ({
  tour,
  mode = 'demo',
  onComplete,
  onExit,
  onStepView,
  onCta,
}) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [ready, setReady] = useState(false);
  const anchorEl = useRef<HTMLElement | null>(null);
  const lastCallout = useRef<{
    overlay: TourOverlay;
    rect: AnchorRect | null;
    stepNumber: number;
  } | null>(null);
  // The last step we counted a view for, so one visit never counts twice.
  const viewedStepId = useRef<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const step = tour.steps[index];
  const overlay = step?.overlays[0];

  // Navigate first; the effect below re-runs once the pathname matches.
  useEffect(() => {
    if (!step) return;
    if (location.pathname !== step.targetRoute) {
      navigate(step.targetRoute);
    }
  }, [step, location.pathname, navigate]);

  // Prepare the step: preActions, then locate and measure the anchor.
  useEffect(() => {
    if (!step || !overlay) return;
    if (location.pathname !== step.targetRoute) return;

    let cancelled = false;
    setReady(false);
    setRect(null);
    anchorEl.current = null;

    (async () => {
      for (const action of step.preActions ?? []) {
        if (cancelled) return;
        if (action.type === 'wait') {
          await sleep(action.ms);
          continue;
        }
        if (action.onlyIfElementExists && !document.querySelector(action.onlyIfElementExists)) {
          continue;
        }
        const target = action.untilVisible
          ? await clickUntilVisible(action.selector, action.untilVisible)
          : await waitForSelector(action.selector);
        if (cancelled) return;
        const backupTarget =
          !target && action.untilVisible && action.backupSelector
            ? await clickUntilVisible(action.backupSelector, action.untilVisible)
            : null;
        if (cancelled) return;
        const foundTarget = target ?? backupTarget;
        if (foundTarget && action.untilVisible) {
          await clickUntilCentered(
            target ? action.selector : (action.backupSelector ?? action.selector),
            target ? action.backupSelector : action.selector,
            action.untilVisible,
          );
          if (cancelled) return;
        }
        const clickTarget = action.untilVisible
          ? (document.querySelector<HTMLElement>(action.untilVisible) ?? foundTarget)
          : foundTarget;
        if (clickTarget) {
          clickTarget.click();
          await sleep(250); // let the click's render settle
        } else if (!action.optional) {
          console.warn(`[tour] preAction target missing: ${action.selector}`);
        }
      }

      if (cancelled) return;

      if (overlay.type === 'tooltip' && overlay.anchor) {
        const el = await waitForSelector(overlay.anchor.selector);
        if (cancelled) return;
        if (el) {
          anchorEl.current = el;
          // A preAction may still be reflowing the page, and scrolling to where the
          // anchor is mid-animation aims at the wrong place.
          await waitForStableRect(el);
          if (cancelled) return;
          scrollIntoView(el);
          await waitForScrollSettle();
          if (cancelled) return;
          setRect(measure(el));
        }
      }

      setReady(true);
      if (viewedStepId.current !== step.id) {
        viewedStepId.current = step.id;
        onStepView?.(step.id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, overlay, location.pathname, onStepView]);

  // Keep an anchored callout attached while the page moves under it.
  useEffect(() => {
    if (!ready || !anchorEl.current) return;
    const reposition = () => {
      if (anchorEl.current) setRect(measure(anchorEl.current));
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [ready, index]);

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i + 1 >= tour.steps.length) {
        onComplete?.();
        return i;
      }
      return i + 1;
    });
  }, [tour.steps.length, onComplete]);

  const goBack = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  const handleAction = useCallback(
    (action: TourAction) => {
      switch (action.type) {
        case 'next':
          // The opening modal's "Next" button is really its own CTA, so track it
          // as one too.
          if (action.label.toLowerCase() === 'start the tour') onCta?.(action.label);
          goNext();
          break;
        case 'back':
          goBack();
          break;
        case 'end':
          onComplete?.();
          break;
        case 'restart':
          setIndex(0);
          break;
        case 'cta':
          // New tab, so an embedded demo is not navigated away from.
          if (action.href) window.open(action.href, '_blank', 'noopener');
          onCta?.(action.label);
          break;
      }
    },
    [goNext, goBack, onComplete, onCta],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goBack();
      else if (e.key === 'Escape') onExit?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goBack, onExit]);

  const nextCallout =
    step && overlay && ready
      ? {
          overlay: {
            ...overlay,
            body:
              mode === 'onboarding' && overlay.onboardingBody
                ? overlay.onboardingBody
                : overlay.body,
          },
          rect,
          stepNumber: index + 1,
        }
      : null;

  // Keep the previous card up while the next step prepares, instead of blanking. Its
  // anchor has usually moved by now — a preAction reflowing the page, say — so centre
  // it rather than pin it to a position that no longer exists.
  if (nextCallout) lastCallout.current = nextCallout;
  const callout =
    nextCallout ?? (lastCallout.current ? { ...lastCallout.current, rect: null } : null);
  if (!callout) return null;

  return (
    <TourCallout
      overlay={callout.overlay}
      rect={callout.rect}
      stepNumber={callout.stepNumber}
      stepCount={tour.steps.length}
      onAction={handleAction}
      onBodyLinkClick={onCta}
    />
  );
};

export default TourController;
